/**
 * Small shared confirm dialog — lifts the inline modal pattern used across the
 * app (CRFBuilder delete-form, Sites delete-site) into one reusable component so
 * destructive validation actions get a real "here is what will happen" step.
 *
 * Matches the existing inline pattern: fixed dark overlay, centered white card,
 * click-outside to cancel, a red primary button for destructive actions.
 */
import { Loader2 } from "lucide-react";

interface ConfirmDialogProps {
  title: string;
  /** Body — a string, or arbitrary nodes for a richer "what will happen". */
  children: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  /** Red destructive styling when true; neutral/indigo otherwise. */
  destructive?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ConfirmDialog({
  title,
  children,
  confirmLabel,
  cancelLabel = "Cancel",
  destructive = false,
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={busy ? undefined : onCancel}
    >
      <div
        className="bg-white rounded-xl p-6 max-w-md w-full shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-3">{title}</h3>
        <div className="text-sm text-gray-600 space-y-2 mb-6">{children}</div>
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className={
              "px-4 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50 inline-flex items-center gap-2 " +
              (destructive ? "bg-red-600 hover:bg-red-700" : "bg-indigo-600 hover:bg-indigo-700")
            }
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
